// client/src/pages/TemplateListPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as templatesApi from '../api/templates'; // Our frontend templates API service
import PageHeader from '../components/PageHeader';

function TemplateListPage({ inTab = false }) {
  const { loading: authLoading } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Calculate pagination
  const totalItems = templates.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTemplates = templates.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // Scroll to top on page change
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page on items per page change
  };

  // Utility function to strip HTML and truncate to first line or a short snippet
  const getDescriptionSnippet = (description) => {
    if (!description) return 'No description';
    // Remove HTML tags
    const plainText = description.replace(/<[^>]*>/g, '');
    // Truncate to first line or a reasonable character limit
    const firstLine = plainText.split('\n')[0];
    return firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;
  };

  useEffect(() => {
    // Fetch templates when component mounts
    const getTemplates = async () => {
      if (authLoading) {
        setLoading(false); // If auth is still loading, stop local loading
        return;
      }

      try {
        setError(null);
        setSuccess(null);
        setLoading(true);
        const response = await templatesApi.fetchTemplates();
        const sortedTemplates = response.templates.sort((a, b) => {
          const typeA = a.type || 'N/A';
          const typeB = b.type || 'N/A';
          return typeA.localeCompare(typeB);
        });
        setTemplates(sortedTemplates); // Assuming API returns { templates: [...] }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        setError(err.message || 'Failed to load templates.');
      } finally {
        setLoading(false);
      }
    };

    getTemplates();
  }, [authLoading]); // Dependency array: re-run when authLoading changes

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return; // User cancelled
    }
    setLoading(true); // Indicate loading while deleting
    try {
      setError(null);
      setSuccess(null);
      await templatesApi.deleteTemplate(templateId);
      setSuccess('Template deleted successfully!');
      // Remove the deleted template from the local state
      setTemplates(prevTemplates => prevTemplates.filter(template => template.id !== templateId));
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError(err.message || 'Failed to delete template.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
        <div className="text-xl text-gray-700">Loading Templates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md max-w-md text-center" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (inTab) {
    return (
      <div className="w-full max-w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6 min-w-0">
          <h2 className="text-lg sm:text-2xl font-bold text-primary-600">Quote/Invoice Templates</h2>
          <Link
            to="/templates/new"
            className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap text-center"
          >
            + Add Section Template
          </Link>
        </div>

          {success && (
            <div className="mt-4 sm:mt-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4 text-sm sm:text-base" role="alert">
              <span className="block sm:inline">{success}</span>
            </div>
          )}

          {templates.length === 0 ? (
            <p className="mt-4 sm:mt-6 text-gray-600 text-center py-6 sm:py-8 border border-dashed border-gray-300 rounded-lg text-sm sm:text-base">
              You haven't created any templates yet. Click "Create New Template" to get started!
            </p>
          ) : (
            <div className="mt-4 sm:mt-6 w-full max-w-full">
            {/* Desktop Table View (hidden on small screens) */}
            <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Template Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedTemplates.map((template) => (
                    <tr key={template.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {template.type || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-[150px] sm:max-w-xs">
                        <div className="truncate" title={getDescriptionSnippet(template.description)}>{getDescriptionSnippet(template.description)}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/templates/edit/${template.id}`}
                            className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                            aria-label={`Edit ${template.name}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                          </Link>
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                            aria-label={`Delete ${template.name}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View (visible on small screens, hidden on md and up) */}
            <div className="md:hidden space-y-4">
              {paginatedTemplates.map((template) => (
                <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative">
                  <div className="flex justify-between items-start gap-2 mb-2 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate flex-1">{template.name}</h3>
                    <div className="flex space-x-1"> {/* Smaller space for mobile icons */}
                      <Link
                        to={`/templates/edit/${template.id}`}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                        aria-label={`Edit ${template.name}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                      </Link>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                        aria-label={`Delete ${template.name}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Type:</span> {template.type || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    <span className="font-medium">Description:</span> <span className="truncate" title={getDescriptionSnippet(template.description)}>{getDescriptionSnippet(template.description)}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {templates.length > 0 && (
            <div className="mt-4 sm:mt-6 flex flex-col items-center space-y-3 sm:flex-row sm:justify-between sm:space-y-0 w-full overflow-x-hidden">
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-600 whitespace-nowrap">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="flex flex-col items-center space-y-2 sm:space-y-0 sm:flex-row sm:space-x-2 w-full sm:w-auto">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 whitespace-nowrap"
                >
                  Previous
                </button>
                <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 whitespace-nowrap"
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
      <div className="max-w-4xl mx-auto w-full">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200 overflow-x-hidden">
          <PageHeader title="Your Templates">
            <Link
              to="/templates/new"
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              + Create New Template
            </Link>
          </PageHeader>

          {success && (
            <div className="mt-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
              <span className="block sm:inline">{success}</span>
            </div>
          )}

          {templates.length === 0 ? (
            <p className="mt-6 text-gray-600 text-center py-8 border border-dashed border-gray-300 rounded-lg">
              You haven't created any templates yet. Click "Create New Template" to get started!
            </p>
          ) : (
            <div className="mt-6">
            {/* Desktop Table View (hidden on small screens) */}
            <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Template Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedTemplates.map((template) => (
                    <tr key={template.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {template.type || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <div className="truncate" title={getDescriptionSnippet(template.description)}>
                          {getDescriptionSnippet(template.description)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/templates/edit/${template.id}`}
                            className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                            aria-label={`Edit ${template.name}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                          </Link>
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                            aria-label={`Delete ${template.name}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View (card layout) */}
            <div className="md:hidden space-y-4">
              {paginatedTemplates.map((template) => (
                <div key={template.id} className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex justify-between items-start gap-2 mb-2 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate flex-1">{template.name}</h4>
                    <div className="flex space-x-2">
                      <Link
                        to={`/templates/edit/${template.id}`}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                        aria-label={`Edit ${template.name}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                      </Link>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                        aria-label={`Delete ${template.name}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Type:</span> {template.type || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    <span className="font-medium">Description:</span> <span className="truncate" title={getDescriptionSnippet(template.description)}>{getDescriptionSnippet(template.description)}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {templates.length > 0 && (
          <div className="mt-6 flex flex-col items-center space-y-4 sm:flex-row sm:justify-between sm:space-y-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages} ({startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items)
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
            </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default TemplateListPage;
